import React from "react";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/joy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import CloseIcon from "@mui/icons-material/Close";
import ExtensionIcon from "@mui/icons-material/Extension";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import LanguageIcon from "@mui/icons-material/Language";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PaletteIcon from "@mui/icons-material/Palette";
import PaymentIcon from "@mui/icons-material/Payment";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SettingsIcon from "@mui/icons-material/Settings";
import StorefrontIcon from "@mui/icons-material/Storefront";
import TuneIcon from "@mui/icons-material/Tune";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import { Link, useLocation } from "react-router-dom";
import { useOrgSettingsContext } from "../providers/OrgSettingsProvider";

interface OrgSettingsSidebarProps {
  isCollapsed: boolean;
  isMobile?: boolean;
  onClose?: () => void;
}

interface NavItem {
  path: string;
  name: string;
  icon: React.ComponentType;
  exact?: boolean;
}

const OrgSettingsSidebar: React.FC<OrgSettingsSidebarProps> = ({
  isCollapsed,
  isMobile = false,
  onClose,
}) => {
  const location = useLocation();
  const { organizationDetails, isEnterpriseMode, isEmailAdminEnabled } =
    useOrgSettingsContext();

  const navItems: NavItem[] = [
    {
      path: "/organization/settings",
      name: "Overview",
      icon: TuneIcon,
      exact: true,
    },
    {
      path: "/organization/settings/members",
      name: "Members",
      icon: PeopleAltIcon,
    },
    ...(!isEnterpriseMode
      ? [
          {
            path: "/organization/settings/domains",
            name: "Domains",
            icon: LanguageIcon,
          },
          {
            path: "/organization/settings/billing",
            name: "Billing",
            icon: PaymentIcon,
          },
        ]
      : []),
    {
      path: "/organization/settings/branding",
      name: "Branding",
      icon: PaletteIcon,
    },
    ...(isEmailAdminEnabled
      ? [
          {
            path: "/organization/settings/email",
            name: "Email",
            icon: MailOutlineIcon,
          },
        ]
      : []),
    {
      path: "/organization/settings/agent-skills",
      name: "Agent Skills",
      icon: StorefrontIcon,
    },
    {
      path: "/organization/settings/integrations",
      name: "Integrations",
      icon: ExtensionIcon,
    },
    {
      path: "/organization/settings/heartbeats",
      name: "Heartbeats",
      icon: FavoriteBorderIcon,
    },
    {
      path: "/organization/settings/dashboards",
      name: "Module dashboards",
      icon: QueryStatsIcon,
    },
  ];

  return (
    <Box
      component="aside"
      sx={{
        width: isMobile ? "100%" : isCollapsed ? "64px" : "250px",
        minWidth: isMobile ? "auto" : isCollapsed ? "64px" : "250px",
        maxWidth: isMobile ? "100%" : isCollapsed ? "64px" : "250px",
        position: isMobile ? undefined : "sticky",
        top: isMobile ? undefined : "var(--header-height)",
        height: isMobile ? "100%" : "calc(100vh - var(--header-height))",
        backgroundColor: "background.surface",
        borderRight: isMobile ? "none" : "1px solid",
        borderColor: "divider",
        transition: isMobile
          ? "none"
          : "width 0.3s ease, min-width 0.3s ease, max-width 0.3s ease",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        "& *": { overflowX: "hidden" },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: isMobile ? 2 : isCollapsed ? 1.5 : 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.level1",
          display: "flex",
          alignItems: "center",
          justifyContent: isMobile
            ? "space-between"
            : isCollapsed
              ? "center"
              : "flex-start",
        }}
      >
        {!isCollapsed ? (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ flex: 1, minWidth: 0, maxWidth: "100%" }}
          >
            <Avatar
              size="sm"
              sx={{
                bgcolor: "primary.500",
                fontSize: "0.75rem",
                flexShrink: 0,
              }}
            >
              <BusinessCenterIcon sx={{ color: "#ffffff" }} />
            </Avatar>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                maxWidth: "100%",
                overflow: "hidden",
              }}
            >
              <Typography
                level="body-xs"
                color="neutral"
                sx={{
                  mb: 0.25,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  transition: "none",
                }}
              >
                Organization Settings
              </Typography>
              {organizationDetails?.name && (
                <Tooltip title={organizationDetails.name} placement="top">
                  <Typography
                    level="body-sm"
                    sx={{
                      fontWeight: "md",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: "text.primary",
                      cursor: "default",
                      transition: "none",
                    }}
                  >
                    {organizationDetails.name}
                  </Typography>
                </Tooltip>
              )}
            </Box>
          </Stack>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Avatar
              size="sm"
              sx={{ bgcolor: "primary.500", fontSize: "0.75rem" }}
            >
              <BusinessCenterIcon sx={{ color: "#ffffff" }} />
            </Avatar>
          </Box>
        )}

        {isMobile && onClose && (
          <IconButton
            size="sm"
            variant="plain"
            onClick={onClose}
            sx={{
              color: "text.primary",
              "&:hover": { backgroundColor: "background.level2" },
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, overflow: "auto", minWidth: 0, maxWidth: "100%" }}>
        <List
          size="sm"
          sx={{
            "--ListItem-radius": "8px",
            "--List-gap": "4px",
            p: 1,
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          {/* Back to Dashboard */}
          <ListItem>
            <ListItemButton
              component={Link}
              to="/"
              onClick={isMobile ? onClose : undefined}
              sx={{
                justifyContent: isCollapsed ? "center" : "flex-start",
                minHeight: "40px",
              }}
            >
              <ArrowBackIcon />
              {!isCollapsed && (
                <Typography
                  sx={{ ml: 1, whiteSpace: "nowrap", transition: "none" }}
                >
                  Dashboard
                </Typography>
              )}
            </ListItemButton>
          </ListItem>

          {navItems.map((item) => {
            const isActive = item.exact
              ? location.pathname === item.path
              : location.pathname === item.path ||
                location.pathname.startsWith(item.path + "/");
            const IconComponent = item.icon;

            return (
              <ListItem key={item.path}>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  selected={isActive}
                  onClick={isMobile ? onClose : undefined}
                  sx={{
                    justifyContent: isCollapsed ? "center" : "flex-start",
                    minHeight: "40px",
                  }}
                >
                  <IconComponent />
                  {!isCollapsed && (
                    <Typography
                      sx={{ ml: 1, whiteSpace: "nowrap", transition: "none" }}
                    >
                      {item.name}
                    </Typography>
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}

          <Divider sx={{ my: 1 }} />

          {/* Advanced — destructive actions, kept separate */}
          {(() => {
            const advancedPath = "/organization/settings/advanced";
            const isActive = location.pathname === advancedPath;
            return (
              <ListItem>
                <ListItemButton
                  component={Link}
                  to={advancedPath}
                  selected={isActive}
                  onClick={isMobile ? onClose : undefined}
                  sx={{
                    justifyContent: isCollapsed ? "center" : "flex-start",
                    minHeight: "40px",
                  }}
                >
                  <SettingsIcon />
                  {!isCollapsed && (
                    <Typography
                      sx={{ ml: 1, whiteSpace: "nowrap", transition: "none" }}
                    >
                      Advanced
                    </Typography>
                  )}
                </ListItemButton>
              </ListItem>
            );
          })()}
        </List>
      </Box>
    </Box>
  );
};

export default OrgSettingsSidebar;
